/**
 * Hook de suivi des conversions Google Analytics GA4
 * Centralise tous les événements de conversion du site VEA STUDIO
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Vérifie si l'utilisateur a consenti aux cookies analytics
function hasAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const stored = localStorage.getItem("veastudio-cookie-consent");
    if (!stored) return false; // Pas encore de décision
    const consent = JSON.parse(stored);
    return consent.analytics === true;
  } catch {
    return false;
  }
}

// Fonction utilitaire pour envoyer un événement GA4 (conditionné au consentement)
function sendGAEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (!hasAnalyticsConsent()) return; // Bloquer si pas de consentement
  window.gtag("event", eventName, params);
}

export function useAnalytics() {
  /**
   * Suivi de la soumission d'un formulaire de devis
   * @param category - Catégorie de la prestation (mariage, immobilier, entreprise, association)
   */
  const trackDevisSubmit = (category?: string) => {
    sendGAEvent("generate_lead", {
      event_category: "conversion",
      event_label: category ? `devis_${category}` : "devis_general",
      value: 1,
    });
    // Événement de conversion principal GA4
    sendGAEvent("conversion", {
      send_to: "G-B50QB7GW3Q",
      event_category: "formulaire_devis",
      event_label: category || "general",
    });
  };

  /**
   * Suivi d'un clic sur un bouton WhatsApp
   * @param source - Page ou section d'origine du clic
   * @param service - Service concerné (optionnel)
   */
  const trackWhatsAppClick = (source: string, service?: string) => {
    sendGAEvent("contact", {
      event_category: "engagement",
      event_label: `whatsapp_${source}`,
      method: "whatsapp",
      service: service || "general",
    });
    sendGAEvent("conversion", {
      send_to: "G-B50QB7GW3Q",
      event_category: "contact_whatsapp",
      event_label: source,
    });
  };

  /**
   * Suivi d'un clic sur un lien email
   * @param source - Page ou section d'origine du clic
   */
  const trackEmailClick = (source: string) => {
    sendGAEvent("contact", {
      event_category: "engagement",
      event_label: `email_${source}`,
      method: "email",
    });
  };

  /**
   * Suivi d'un clic sur un lien téléphone
   * @param source - Page ou section d'origine du clic
   */
  const trackPhoneClick = (source: string) => {
    sendGAEvent("contact", {
      event_category: "engagement",
      event_label: `phone_${source}`,
      method: "phone",
    });
  };

  /**
   * Suivi d'un clic sur le bouton "Laisser un avis Google"
   */
  const trackGoogleReviewClick = () => {
    sendGAEvent("click", {
      event_category: "engagement",
      event_label: "google_review_button",
      link_url: "https://g.page/r/CVL94HrdFpMfEBM/review",
    });
  };

  /**
   * Suivi de l'utilisation du calculateur de prix
   * @param category - Catégorie sélectionnée
   * @param format - Format sélectionné (drone, camera, combo)
   * @param estimatedPrice - Fourchette de prix estimée
   */
  const trackCalculatorUse = (category: string, format?: string, estimatedPrice?: string) => {
    sendGAEvent("calculator_use", {
      event_category: "engagement",
      event_label: `calculator_${category}`,
      format: format || "unknown",
      estimated_price: estimatedPrice || "unknown",
    });
  };

  /**
   * Suivi de la complétion du quiz de conversion
   * @param result - Résultat du quiz (catégorie recommandée)
   */
  const trackQuizComplete = (result: string) => {
    sendGAEvent("quiz_complete", {
      event_category: "engagement",
      event_label: `quiz_result_${result}`,
      quiz_result: result,
    });
  };

  /**
   * Suivi de la soumission d'un témoignage
   */
  const trackTestimonialSubmit = () => {
    sendGAEvent("submit", {
      event_category: "engagement",
      event_label: "testimonial_submit",
    });
  };

  /**
   * Suivi de la vue d'une page de ville (SEO local)
   * @param city - Nom de la ville
   */
  const trackCityPageView = (city: string) => {
    sendGAEvent("page_view_city", {
      event_category: "seo_local",
      event_label: `city_${city.toLowerCase().replace(/\s+/g, "_")}`,
      city_name: city,
    });
  };

  /**
   * Suivi d'un partage sur les réseaux sociaux
   * @param network - Réseau social (facebook, linkedin, twitter, copy_link)
   * @param articleTitle - Titre de l'article partagé
   */
  const trackSocialShare = (network: string, articleTitle?: string) => {
    sendGAEvent("share", {
      event_category: "engagement",
      event_label: `share_${network}`,
      method: network,
      content_type: "article",
      item_id: articleTitle || "unknown",
    });
  };

  /**
   * Suivi d'un clic sur un bouton "Voir les tarifs"
   * @param source - Page d'origine
   */
  const trackTarifsClick = (source: string) => {
    sendGAEvent("click", {
      event_category: "navigation",
      event_label: `tarifs_from_${source}`,
    });
  };

  return {
    trackDevisSubmit,
    trackWhatsAppClick,
    trackEmailClick,
    trackPhoneClick,
    trackGoogleReviewClick,
    trackCalculatorUse,
    trackQuizComplete,
    trackTestimonialSubmit,
    trackCityPageView,
    trackTarifsClick,
    trackSocialShare,
  };
}
